import PropTypes from 'prop-types';
import React, { useContext, useState, useMemo, useCallback } from 'react';
import { useTranslation } from "react-i18next";
import { useDispatch } from 'react-redux';
import { LoadingContext, M_StateActions, MTable } from 'src/M_Components';

export default function NonSpotTenderConfig({ roles, onCreateTab, toastMessages }) {
    const ctx = useContext(LoadingContext);
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [refresh, setRefresh] = useState(false);
    const removeContextItems = useMemo(() => ['Print', 'Copy', 'Create'], []); // keep identity stable

    // Centralize CRUD route to avoid hard-coded duplication and typos
    const CRUD_ROUTE = 'tenders/TenderConfigCrud';

    //===============================================================================================================
    // openNonSpotTenderCrud:
    // - Stable (useCallback) handler that opens the Non-Spot Tender CRUD tab.
    // - Persists 'operation' in global state; for View/Edit/Copy also persists the selected row as 'TenderLine'.
    // - Validates the presence of a row for operations that require it and shows a localized toast if missing.
    // - Builds the tab label using i18n and the tender's RequestForQuotes.
    // - Using a stable callback prevents MTable from re-rendering/refetching due to prop identity changes.
    //===============================================================================================================
    const openNonSpotTenderCrud = useCallback(
        (operation, row) => {
            // Persist operation in global state
            dispatch(M_StateActions.addM_State({ key: 'operation', value: operation }));

            // For the operations below, must be provided row/entity data
            if (['Edit', 'Copy', 'View'].includes(operation)) {
                if (!row) {
                    toastMessages('error', '', t('SelectedLine')); // If no data for these operations, show an error
                    return;
                }
                // Persist the selected Tender line data as 'TenderLine'
                dispatch(M_StateActions.addM_State({ key: 'TenderLine', value: row }));
            }

            // Build the tab label name
            const tabLabel =
                operation === 'Create'
                    ? t('LabelNewTender')
                    : operation === 'View'
                    ? `${t('LabelViewTender')} "${row?.RequestForQuotes ?? ''}"`
                    : `${t('LabelEditTender')} "${row?.RequestForQuotes ?? ''}"`;

            onCreateTab(CRUD_ROUTE, tabLabel); // Open/focus the CRUD tab
        },
        [dispatch, onCreateTab]
    );

    //===============================================================================================================
    // replaceFunctions:
    // - Memoized Map consumed by MTable to handle context actions.
    // - Kept stable with useMemo to avoid unnecessary child rerenders and duplicate fetches.
    //===============================================================================================================
    const replaceFunctions = useMemo(
        () =>
            new Map([
                ['View', (row) => openNonSpotTenderCrud('View', row)],
                ['Edit', (row) => openNonSpotTenderCrud('Edit', row)],
            ]),
        [openNonSpotTenderCrud]
    );

    return (
        <div className='divGlobalSettings'>
            <div className='subTitleSettings'>
                <div>
                    <div style={{ color: ctx.secondaryColor }}>{t("NonSpotTenders")}</div>
                    <div className='labelSubtitleSettings'>{t("InfoNonSpotTenders")}</div>
                </div>
            </div>
            <MTable // Dynamic table for non-spot tenders
                filtersOnTable
                entityName={'Tenders'} // Entity name for the table, Tenders in this case
                roleName={'Tenders'} // Role name for permission checks
                rolePermissions={roles?.map(vl => vl.Value)} // User roles for permission management  for RequestForQuotes Module
                removeContextItems={removeContextItems}
                refresh={refresh}
                setRefresh={setRefresh}
                useSessionStorage={false}
                FILTER={"TenderTypeId eq 2"} // Filter Tender table for non-spot tenders only
                replaceFunctions={replaceFunctions} // Custom action handlers
                tabsRailWidth={250}
            />
        </div>
    );
}

NonSpotTenderConfig.propTypes = {
    roles: PropTypes.array,
    onCreateTab: PropTypes.func,
    toastMessages: PropTypes.func
};
